import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categoryBySlug } from "../data/categories.js";
import { imagesForProduct } from "../data/productMedia.js";
import { useQuote } from "../context/QuoteContext.js";

const ProductGroupPage = () => {
  const { categorySlug, productSlug } = useParams();
  const category = categoryBySlug[categorySlug];
  const product = category?.products.find(
    (entry) => entry.slug === productSlug,
  );
  const images = useMemo(
    () => (product ? imagesForProduct(product) : []),
    [product],
  );
  const [currentImage, setCurrentImage] = useState(0);
  const { addItem, items } = useQuote();

  useEffect(() => {
    setCurrentImage(0);
  }, [productSlug]);

  if (!category || !product) {
    return (
      <main className="min-h-screen bg-containx-navy p-6 text-center text-white">
        <h1 className="mb-4 text-3xl font-bold">Product not found</h1>
        <Link to="/products" className="underline">
          View all products
        </Link>
      </main>
    );
  }

  const unitLabel = product.unitLabel || "pieces";

  return (
    <main className="min-h-screen bg-containx-navy p-4 text-white md:p-6">
      <div className="mx-auto max-w-6xl">
        <nav className="mb-6 text-sm">
          <Link to="/products" className="underline">
            Products
          </Link>
          <span aria-hidden="true"> / </span>
          <Link to={`/${category.slug}`} className="underline">
            {category.name}
          </Link>
          <span aria-hidden="true"> / </span>
          <span>{product.name}</span>
        </nav>

        <div className="flex flex-col gap-8 lg:flex-row">
          <section className="flex min-h-64 items-center justify-center lg:w-1/2">
            {images.length > 0 ? (
              <div className="relative flex h-72 w-full items-center justify-center">
                <img
                  src={images[currentImage]}
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                />
                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous image"
                      onClick={() =>
                        setCurrentImage(
                          (currentImage - 1 + images.length) % images.length,
                        )
                      }
                      className="absolute left-2 rounded bg-gray-800 p-3"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      aria-label="Next image"
                      onClick={() =>
                        setCurrentImage((currentImage + 1) % images.length)
                      }
                      className="absolute right-2 rounded bg-gray-800 p-3"
                    >
                      ›
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="flex h-64 w-full items-center justify-center rounded-lg bg-white/10 p-8 text-center text-3xl font-semibold">
                {product.name}
              </div>
            )}
          </section>

          <section className="lg:w-1/2">
            <h1 className="mb-4 text-3xl font-bold">{product.name}</h1>
            {product.description && (
              <p className="mb-6">{product.description}</p>
            )}
            <div className="rounded-lg bg-white/10 p-5">
              <h2 className="mb-2 text-xl font-semibold">Ready to order?</h2>

              <p className="mb-4">
                Call or text us with the SKU and quantity you need.
              </p>

              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href="tel:3019698766"
                  className="rounded-lg bg-white px-6 py-3 text-center font-semibold text-containx-navy"
                >
                  Call 301-969-8766
                </a>

                <a
                  href="sms:3019698766"
                  className="rounded-lg bg-containx-beige px-6 py-3 text-center font-semibold text-containx-navy"
                >
                  Text Us
                </a>

                <a
                  href="/pdf/product-catalog.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-white px-6 py-3 text-center font-semibold text-white"
                >
                  View Full Catalog
                </a>
              </div>
            </div>
          </section>
        </div>

        <section className="mt-10">
          <h2 className="mb-3 text-2xl font-semibold">Specifications</h2>
          <div className="overflow-x-auto">
            <table className="w-full table-auto overflow-hidden rounded-lg bg-white text-left text-containx-navy">
              <thead className="bg-containx-beige text-containx-navy">
                <tr>
                  <th className="border px-4 py-2">SKU</th>
                  <th className="border px-4 py-2">Description</th>
                  <th className="border px-4 py-2">Case Quantity</th>
                  <th className="border px-4 py-2"><span className="sr-only">Quote action</span></th>
                </tr>
              </thead>
              <tbody>
                {product.items.map((entry, index) => (
                  <tr
                    key={entry.sku}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
                  >
                    <td className="border border-gray-300 px-4 py-2 font-semibold text-containx-navy">
                      {entry.sku}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-containx-navy">
                      {entry.description}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-containx-navy">
                      {entry.quantity == null
                        ? "Contact us"
                        : `${entry.quantity.toLocaleString()} ${entry.unitLabel || unitLabel}`}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => addItem({
                          id: `${category.slug}/${product.slug}/${entry.sku}`,
                          sku: entry.sku,
                          productName: product.name,
                          description: entry.description,
                          caseQuantity: entry.quantity == null ? "Contact us" : `${entry.quantity.toLocaleString()} ${entry.unitLabel || unitLabel}`,
                        })}
                        className="whitespace-nowrap rounded-md bg-containx-navy px-4 py-2 text-sm font-bold text-white transition hover:bg-containx-charcoal"
                      >
                        {items.some((item) => item.id === `${category.slug}/${product.slug}/${entry.sku}`) ? "Add Another" : "Add to Quote"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ProductGroupPage;
